import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthPageShell, BackButton, Button } from '@/components';
import { getConfig } from '@/config.ts';
import { useAuthFetch } from '@/hooks';
import type { ConversationDetail, CreatePaymentCheckoutResponse } from '@dermalink/shared';
import styles from './Payment.module.css';

function formatEuroFromCents(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function Payment() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const returnedFromMollie = searchParams.get('source') === 'mollie';
  const conversationId = id ?? '';

  const { data: conversation, isPending } = useQuery<ConversationDetail>({
    queryKey: ['conversation', conversationId],
    enabled: Boolean(conversationId),
    queryFn: async () => {
      const res = await authFetch(`/conversations/${conversationId}`);
      if (!res.ok) throw new Error();
      return (await res.json()) as ConversationDetail;
    },
    refetchInterval: returnedFromMollie ? 2500 : false,
  });

  const amountCents = conversation?.payment_amount_cents ?? 0;
  const amountLabel = useMemo(() => formatEuroFromCents(amountCents), [amountCents]);

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!conversationId) return '';
      const res = await authFetch(`/conversations/${conversationId}/payments/create-checkout`, { method: 'POST' });
      if (!res.ok) {
        let message = 'Betaling starten mislukt. Probeer het opnieuw.';
        try {
          const body = (await res.json()) as { message?: string };
          if (typeof body.message === 'string' && body.message.trim()) {
            message = body.message.trim();
          }
        } catch {
          // Ignore parse errors and keep fallback message.
        }
        throw new Error(message);
      }
      const body = (await res.json()) as CreatePaymentCheckoutResponse;
      return body.checkout_url;
    },
    onSuccess: (checkoutUrl) => {
      window.location.assign(checkoutUrl);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Betaling starten mislukt. Probeer het opnieuw.');
    },
  });

  useEffect(() => {
    if (!conversationId || !conversation) return;
    const paymentDone = conversation.payment_status === 'paid';
    const noPaymentNeeded = conversation.payment_status === 'not_required';
    if (paymentDone || noPaymentNeeded) {
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      navigate(`/conversations/${conversationId}`, { replace: true });
    }
  }, [conversation, conversationId, navigate, queryClient]);

  return (
    <AuthPageShell maxWidth="480px">
      <h1 className="page-heading">Betaling</h1>
      <p className={styles.subheading}>
        Je betaling loopt via Mollie. Na bevestiging sturen we je automatisch terug naar het gesprek.
      </p>

      <BackButton onClick={() => navigate(-1)} label="Terug naar gesprek" className={styles.backBtn} />

      <div className={styles.card}>
        <div className={styles.amountRow}>
          <span className={styles.amountLabel}>Consultatie</span>
          <span className={styles.amountValue}>{amountLabel}</span>
        </div>

        {returnedFromMollie && conversation?.payment_status !== 'paid' && (
          <p className={styles.disclaimer}>
            Betaling wordt bevestigd... Zodra Mollie bevestigt, sluiten we dit venster automatisch.
          </p>
        )}

        {error && <p className="text-muted">{error}</p>}
        {isPending && <p className="text-muted">Betaling voorbereiden...</p>}

        {getConfig().payments_enabled ? (
          <>
            <Button
              type="button"
              variant="primary"
              size="lg"
              disabled={checkoutMutation.isPending || isPending || !conversationId || amountCents <= 0}
              onClick={() => checkoutMutation.mutate()}
            >
              {checkoutMutation.isPending
                ? 'Doorsturen...'
                : `${returnedFromMollie ? 'Opnieuw naar Mollie' : 'Naar Mollie'} — ${amountLabel}`}
            </Button>

            <p className={styles.disclaimer}>
              Je wordt veilig doorgestuurd naar Mollie om de betaling te voltooien.
            </p>
          </>
        ) : (
          <p className="text-muted">
            Online betalen is momenteel nog niet beschikbaar. Je arts neemt contact met je op.
          </p>
        )}
      </div>
    </AuthPageShell>
  );
}
